// ─────────────────────────────────────────
// LogRound.js — post a round
// ─────────────────────────────────────────
// Writes a new row to the 'rounds' table in Supabase.
//
// Course autocomplete:
//   Hits the Golf Course API (golfcourseapi.com) live as the user types.
//   ~30,000 courses worldwide. Results are also cached in our Supabase
//   'courses' table so the leaderboard/passport pages keep working.
//   API key lives in .env.local as REACT_APP_GOLF_API_KEY.
//
// Photo upload:
//   If a photo is attached, it goes to Supabase Storage first,
//   then the public URL is saved in the round row.

import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function LogRound({ session }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // If coming from a CoursePage "Log a Round Here" button,
  // the course name is pre-filled via ?course= in the URL
  const prefilledCourse = searchParams.get('course') || ''

  const [form, setForm] = useState({
    course_name: prefilledCourse,
    score: '',
    par: '72',
    fairways: '',
    gir: '',
    putts: '',
    caption: '',
    data_source: 'Manual'
  })

  // ── Handicap scoring ─────────────────────────────────
  const [slope, setSlope] = useState('113')
  const [handicapIndex, setHandicapIndex] = useState('')

  // Pre-fill handicap index from user profile
  useEffect(() => {
    async function loadProfile() {
      const { data } = await supabase
        .from('profiles')
        .select('handicap')
        .eq('id', session.user.id)
        .single()
      if (data?.handicap != null) setHandicapIndex(String(data.handicap))
    }
    loadProfile()
  }, [])

  // Derived handicap values — computed on every render
  const grossScore = parseInt(form.score) || null
  const hcpIdx = parseFloat(handicapIndex)
  const slopeVal = parseInt(slope) || 113
  const courseHandicap = !isNaN(hcpIdx) ? Math.round(hcpIdx * (slopeVal / 113)) : null
  const netScore = (grossScore != null && courseHandicap != null) ? grossScore - courseHandicap : null

  // ── Course autocomplete state ────────────────────────
  const [suggestions, setSuggestions] = useState([])   // list of matching course names
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const dropdownRef = useRef(null)  // used to detect clicks outside the dropdown

  // ── Media state (photo or video) ──────────────────────
  const [media, setMedia] = useState(null)
  const [mediaPreview, setMediaPreview] = useState(null)
  const [mediaIsVideo, setMediaIsVideo] = useState(false)

  // ── Tournament linking ────────────────────────────────
  const [liveTournaments, setLiveTournaments] = useState([]) // { tournament, member, rounds }
  const [selectedTournamentId, setSelectedTournamentId] = useState('')
  const [selectedRoundId, setSelectedRoundId] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Fetch active tournaments where user is a member OR creator
  useEffect(() => {
    async function fetchLiveTournaments() {
      // 1. Tournaments where user is a tournament_member
      const { data: memberRows } = await supabase
        .from('tournament_members')
        .select('id, tournament_id, display_name')
        .eq('user_id', session.user.id)

      // 2. Active tournaments created by user (admin may not be in tournament_members)
      const { data: createdRows } = await supabase
        .from('tournaments')
        .select('id, name, status, group_id')
        .eq('created_by', session.user.id)
        .eq('status', 'active')

      // Build a map of tournament_id → member row (null if admin-only)
      const memberMap = {}
      for (const r of memberRows || []) memberMap[r.tournament_id] = r

      // Collect all active tournament IDs (deduped)
      const createdIds = new Set((createdRows || []).map(t => t.id))
      const memberTournamentIds = (memberRows || []).map(r => r.tournament_id)
      const allIds = [...new Set([...memberTournamentIds, ...createdIds])]
      if (allIds.length === 0) return

      // Fetch those tournaments' details (to check status for member-only rows)
      const { data: tournamentDetails } = await supabase
        .from('tournaments')
        .select('id, name, status, group_id')
        .in('id', allIds)
        .eq('status', 'active')

      if (!tournamentDetails?.length) return

      // Fetch open rounds for all active tournaments
      const { data: rounds } = await supabase
        .from('tournament_rounds')
        .select('*')
        .in('tournament_id', tournamentDetails.map(t => t.id))
        .neq('status', 'finished')
        .order('round_number')

      setLiveTournaments(tournamentDetails.map(t => {
        const memberRow = memberMap[t.id]
        return {
          tournament: t,
          member: memberRow ? { id: memberRow.id, display_name: memberRow.display_name } : null,
          isAdminOnly: !memberRow && createdIds.has(t.id),
          rounds: (rounds || []).filter(rd => rd.tournament_id === t.id)
        }
      }))
    }
    fetchLiveTournaments()
  }, [])

  // Reset round when tournament changes
  useEffect(() => { setSelectedRoundId('') }, [selectedTournamentId])

  // Close dropdown if user clicks anywhere outside it
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ── Course search (debounced) ────────────────────────
  // Wait 350ms after typing stops before hitting the API.
  // This keeps requests low — we only fire once the user pauses.
  useEffect(() => {
    const query = form.course_name.trim()
    if (query.length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    const timer = setTimeout(() => searchCourses(query), 350)
    return () => clearTimeout(timer)
  }, [form.course_name])

  async function searchCourses(query) {
    setSearchLoading(true)

    const apiKey = process.env.REACT_APP_GOLF_API_KEY

    // ── Try the live Golf Course API first ───────────────
    // Base URL: https://api.golfcourseapi.com/v1
    // Auth:     Authorization: Key {your_key}
    // Endpoint: GET /search?search_query={query}
    if (apiKey) {
      try {
        const res = await fetch(
          `https://api.golfcourseapi.com/v1/search?search_query=${encodeURIComponent(query)}`,
          {
            headers: {
              'Authorization': `Key ${apiKey}`
            }
          }
        )

        if (res.ok) {
          const json = await res.json()
          // API returns: { courses: [{ id, club_name, course_name, location: { address } }] }
          const courses = (json.courses || []).slice(0, 8).map(c => {
            // course_name can be just "Black" — combine with club_name for a full name
            const hasTwoParts = c.club_name && c.course_name && c.club_name !== c.course_name
            return {
              name: hasTwoParts ? `${c.club_name} - ${c.course_name}` : (c.course_name || c.club_name),
              club: null, // name already includes club context, no redundant subtitle needed
              location: c.location?.address || null,
              par: null // API doesn't return par — user fills it in
            }
          })
          setSuggestions(courses)
          setShowSuggestions(true)
          setSearchLoading(false)
          return
        }
      } catch (err) {
        // Network error — fall through to Supabase fallback below
        console.warn('Golf API error, falling back to local cache:', err)
      }
    }

    // ── Fallback: search our local Supabase courses cache ─
    // This runs if: no API key set yet, API is down, or rate limited.
    const { data } = await supabase
      .from('courses')
      .select('name, location, par')
      .ilike('name', `%${query}%`)
      .order('name')
      .limit(8)

    setSuggestions((data || []).map(c => ({
      name: c.name,
      club: null,
      location: c.location,
      par: c.par
    })))
    setShowSuggestions(true)
    setSearchLoading(false)
  }

  function selectCourse(course) {
    setForm(prev => ({
      ...prev,
      course_name: course.name,
      // Only update par if the course has one (API doesn't return par)
      par: course.par ? String(course.par) : prev.par
    }))
    setSuggestions([])
    setShowSuggestions(false)
  }

  // ── Form helpers ─────────────────────────────────────

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  function handleMediaChange(e) {
    const file = e.target.files[0]
    if (!file) return
    const isVideo = file.type.startsWith('video/')
    setMedia(file)
    setMediaPreview(URL.createObjectURL(file))
    setMediaIsVideo(isVideo)
  }

  function removeMedia() {
    setMedia(null)
    setMediaPreview(null)
    setMediaIsVideo(false)
  }

  // ── Submit ───────────────────────────────────────────

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // ── STEP 1: Make sure the course exists in the courses table ──
    // upsert = insert if not there, ignore if already exists
    // This means every course ever logged is automatically catalogued
    await supabase
      .from('courses')
      .upsert(
        { name: form.course_name, par: parseInt(form.par) || null },
        { onConflict: 'name', ignoreDuplicates: true }
      )

    // ── STEP 2: Upload photo or video if one was selected ────────
    let photoUrl = null
    let videoUrl = null
    if (media) {
      const ext = media.name.split('.').pop()
      const filePath = `${session.user.id}/${Date.now()}.${ext}`

      const { error: uploadError } = await supabase.storage
        .from('round-photos')
        .upload(filePath, media)

      if (uploadError) {
        setError('Upload failed: ' + uploadError.message)
        setLoading(false)
        return
      }

      const { data: { publicUrl } } = supabase.storage
        .from('round-photos')
        .getPublicUrl(filePath)

      if (mediaIsVideo) videoUrl = publicUrl
      else photoUrl = publicUrl
    }

    // ── STEP 3: Write the round to Supabase ──────────────────────
    const { error } = await supabase.from('rounds').insert({
      user_id: session.user.id,
      course_name: form.course_name,
      score: parseInt(form.score),
      par: parseInt(form.par) || null,
      fairways: form.fairways ? parseInt(form.fairways) : null,
      gir: form.gir ? parseInt(form.gir) : null,
      putts: form.putts ? parseInt(form.putts) : null,
      caption: form.caption || null,
      data_source: form.data_source,
      photo_url: photoUrl,
      video_url: videoUrl,
      net_score: netScore,
      slope: slopeVal !== 113 ? slopeVal : null,
      handicap_index: !isNaN(hcpIdx) ? hcpIdx : null
    })

    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }

    // ── STEP 4: Link score to tournament if selected ──────────────
    if (selectedTournamentId && selectedRoundId) {
      const linked = liveTournaments.find(t => t.tournament.id === selectedTournamentId)
      if (linked) {
        const score = parseInt(form.score)
        const tournamentId = selectedTournamentId
        const roundId = selectedRoundId

        // If admin-only (not a tournament_member), first find or create their member row
        let memberId = linked.member?.id
        if (!memberId) {
          const { data: existingMember } = await supabase
            .from('tournament_members')
            .select('id')
            .eq('tournament_id', tournamentId)
            .eq('user_id', session.user.id)
            .maybeSingle()

          if (existingMember) {
            memberId = existingMember.id
          } else {
            const { data: newMember } = await supabase
              .from('tournament_members')
              .insert({ tournament_id: tournamentId, user_id: session.user.id, total_points: 0 })
              .select('id').single()
            memberId = newMember?.id
          }
        }

        if (memberId) {
          await supabase.from('tournament_results').upsert({
            tournament_id: tournamentId,
            round_id: roundId,
            member_id: memberId,
            score,
            position: null,
            points_earned: 0
          }, { onConflict: 'round_id,member_id' })

          const { data: matchups } = await supabase
            .from('tournament_matchups')
            .select('id')
            .eq('round_id', roundId)

          if (matchups?.length > 0) {
            await supabase
              .from('tournament_matchup_entries')
              .update({ score })
              .eq('member_id', memberId)
              .in('matchup_id', matchups.map(m => m.id))
          }
        }
      }
    }

    navigate('/')
  }

  const trashTalk = [
    'Finally broke 80 🔥',
    'Pars are my birdies 😂',
    'Course played me today 😅',
    'Someone\'s jealous 👀',
    'Best round of the year 🏆',
    'Course #1 stamped ✈️'
  ]

  // ── RENDER ───────────────────────────────────────────

  return (
    <div style={styles.page}>
      <div style={styles.topbar}>
        <Link to="/" style={styles.back}>← Back</Link>
        <div style={styles.logo}>Log a Round</div>
        <div style={{ width: 60 }} />
      </div>

      <div style={styles.body}>
        <form onSubmit={handleSubmit} style={styles.form}>

          {/* ── COURSE NAME with autocomplete ── */}
          <div style={styles.field}>
            <label style={styles.label}>Course Name *</label>

            {/* Wrapper div gets the ref so we can detect outside clicks */}
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <input
                style={styles.input}
                placeholder="Search for a course..."
                value={form.course_name}
                onChange={e => update('course_name', e.target.value)}
                onFocus={() => form.course_name.length >= 2 && setShowSuggestions(true)}
                required
                autoComplete="off"
              />

              {/* Small spinner shown while searching */}
              {searchLoading && (
                <div style={styles.searchSpinner}>⏳</div>
              )}

              {/* ── DROPDOWN ── */}
              {/* Shows up to 8 matching courses as the user types */}
              {showSuggestions && (
                <div style={styles.dropdown}>
                  {suggestions.length === 0 && !searchLoading && (
                    <div style={styles.dropdownEmpty}>
                      No courses found — just type the name and we'll add it
                    </div>
                  )}
                  {suggestions.map((course, i) => (
                    <div
                      key={`${course.name}-${i}`}
                      style={styles.dropdownItem}
                      onMouseDown={() => selectCourse(course)}
                    >
                      <div style={styles.dropdownName}>⛳ {course.name}</div>
                      {/* Show the club name if different from course name */}
                      {course.club && (
                        <div style={styles.dropdownClub}>{course.club}</div>
                      )}
                      {course.location && (
                        <div style={styles.dropdownLocation}>📍 {course.location}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── SCORE + PAR ── */}
          <div style={styles.field}>
            <label style={styles.label}>Score & Par</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                style={{ ...styles.input, flex: 1, textAlign: 'center', fontSize: 28, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800 }}
                type="number"
                placeholder="82"
                value={form.score}
                onChange={e => update('score', e.target.value)}
                required
                min="18"
                max="200"
              />
              <input
                style={{ ...styles.input, flex: 1, textAlign: 'center', fontSize: 18, fontWeight: 600 }}
                type="number"
                placeholder="Par 72"
                value={form.par}
                onChange={e => update('par', e.target.value)}
                min="54"
                max="74"
              />
            </div>
          </div>

          {/* ── SLOPE + HANDICAP CALCULATION ── */}
          <div style={styles.field}>
            <label style={styles.label}>Handicap Scoring</label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <input
                  style={{ ...styles.input, textAlign: 'center' }}
                  type="number"
                  placeholder="HCP Index"
                  value={handicapIndex}
                  onChange={e => setHandicapIndex(e.target.value)}
                  step="0.1"
                  min="0"
                  max="54"
                />
                <div style={styles.statHint}>HCP Index</div>
              </div>
              <div style={{ flex: 1 }}>
                <input
                  style={{ ...styles.input, textAlign: 'center' }}
                  type="number"
                  placeholder="113"
                  value={slope}
                  onChange={e => setSlope(e.target.value)}
                  min="55"
                  max="155"
                />
                <div style={styles.statHint}>Slope (default 113)</div>
              </div>
            </div>

            {/* ── Calculation preview ── */}
            {grossScore != null && courseHandicap != null && (
              <div style={styles.hcpBox}>
                <div style={styles.hcpTitle}>📊 Handicap Calculation</div>
                <div style={styles.hcpGrid}>
                  <span style={styles.hcpKey}>HCP Index</span>
                  <span style={styles.hcpVal}>{hcpIdx}</span>
                  <span style={styles.hcpKey}>Slope Rating</span>
                  <span style={styles.hcpVal}>{slopeVal}</span>
                  <span style={styles.hcpKey}>Course HCP</span>
                  <span style={styles.hcpVal}>{courseHandicap}</span>
                  <span style={styles.hcpKey}>Gross Score</span>
                  <span style={styles.hcpVal}>{grossScore}</span>
                </div>
                <div style={styles.hcpResult}>
                  Net Score: <strong>{netScore}</strong>
                </div>
              </div>
            )}
          </div>

          {/* ── STATS ── */}
          <div style={styles.field}>
            <label style={styles.label}>Stats (optional)</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <input
                  style={{ ...styles.input, textAlign: 'center' }}
                  type="number" placeholder="FIR"
                  value={form.fairways}
                  onChange={e => update('fairways', e.target.value)}
                  min="0" max="18"
                />
                <div style={styles.statHint}>Fairways</div>
              </div>
              <div style={{ flex: 1 }}>
                <input
                  style={{ ...styles.input, textAlign: 'center' }}
                  type="number" placeholder="GIR"
                  value={form.gir}
                  onChange={e => update('gir', e.target.value)}
                  min="0" max="18"
                />
                <div style={styles.statHint}>Greens</div>
              </div>
              <div style={{ flex: 1 }}>
                <input
                  style={{ ...styles.input, textAlign: 'center' }}
                  type="number" placeholder="Putts"
                  value={form.putts}
                  onChange={e => update('putts', e.target.value)}
                  min="18" max="72"
                />
                <div style={styles.statHint}>Putts</div>
              </div>
            </div>
          </div>

          {/* ── TOURNAMENT LINK ── */}
          {liveTournaments.length > 0 && (
            <div style={styles.field}>
              <label style={styles.label}>Link to Tournament (optional)</label>
              <select
                style={styles.select}
                value={selectedTournamentId}
                onChange={e => setSelectedTournamentId(e.target.value)}
              >
                <option value=''>— No tournament —</option>
                {liveTournaments.map(({ tournament }) => (
                  <option key={tournament.id} value={tournament.id}>{tournament.name}</option>
                ))}
              </select>

              {selectedTournamentId && (() => {
                const linked = liveTournaments.find(t => t.tournament.id === selectedTournamentId)
                const rounds = linked?.rounds || []
                return rounds.length > 0 ? (
                  <select
                    style={styles.select}
                    value={selectedRoundId}
                    onChange={e => setSelectedRoundId(e.target.value)}
                  >
                    <option value=''>— Select round —</option>
                    {rounds.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.name || `Round ${r.round_number}`}
                        {r.scheduled_date ? ` · ${new Date(r.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={styles.tourneyNote}>No open rounds in this tournament.</div>
                )
              })()}

              {selectedTournamentId && selectedRoundId && (
                <div style={styles.tourneyConfirm}>
                  ✅ Score will be submitted to this tournament round
                </div>
              )}
            </div>
          )}

          {/* ── TRASH TALK ── */}
          <div style={styles.field}>
            <label style={styles.label}>Trash Talk</label>
            <textarea
              style={{ ...styles.input, minHeight: 80, resize: 'none' }}
              placeholder="Tell your friends how it went..."
              value={form.caption}
              onChange={e => update('caption', e.target.value)}
              maxLength={280}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {trashTalk.map(t => (
                <button key={t} type="button" style={styles.chip} onClick={() => update('caption', t)}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* ── PHOTO / VIDEO ── */}
          <div style={styles.field}>
            <label style={styles.label}>Photo / Video (optional)</label>
            {mediaPreview ? (
              <div style={styles.photoPreviewWrapper}>
                {mediaIsVideo ? (
                  <video src={mediaPreview} style={{ ...styles.photoPreview, background: '#000', objectFit: 'contain' }} controls playsInline />
                ) : (
                  <img src={mediaPreview} alt="Round preview" style={styles.photoPreview} />
                )}
                <button type="button" style={styles.removePhotoBtn} onClick={removeMedia}>✕</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ ...styles.photoPickerBtn, flex: 1 }}>
                  📷 Photo
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleMediaChange} />
                </label>
                <label style={{ ...styles.photoPickerBtn, flex: 1 }}>
                  🎥 Video
                  <input type="file" accept="video/*" style={{ display: 'none' }} onChange={handleMediaChange} />
                </label>
              </div>
            )}
          </div>

          {/* ── DATA SOURCE ── */}
          <div style={styles.field}>
            <label style={styles.label}>Data Source</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['Manual', 'Arccos', 'TheGrint', 'Garmin'].map(src => (
                <button
                  key={src} type="button"
                  style={{ ...styles.srcBtn, ...(form.data_source === src ? styles.srcBtnActive : {}) }}
                  onClick={() => update('data_source', src)}
                >
                  {src}
                </button>
              ))}
            </div>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button style={styles.submitBtn} type="submit" disabled={loading}>
            {loading ? (media ? `Uploading ${mediaIsVideo ? 'video' : 'photo'}...` : 'Posting...') : '🏌️ Post Round'}
          </button>

        </form>
      </div>
    </div>
  )
}

// ── STYLES ───────────────────────────────────────────────────

const styles = {
  page: { minHeight: '100vh', background: '#f4f7f5', fontFamily: "'Barlow', sans-serif" },
  topbar: {
    background: '#0e1f13', padding: '14px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  back: { color: '#c8f135', textDecoration: 'none', fontSize: 14, fontWeight: 600 },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 20, color: 'white', letterSpacing: 1
  },
  body: { maxWidth: 500, margin: '0 auto', padding: 20 },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 8 },
  label: {
    fontSize: 11, fontWeight: 700, letterSpacing: 2,
    textTransform: 'uppercase', color: '#6b7c6e'
  },
  input: {
    background: 'white', border: '1.5px solid #e2ebe4', borderRadius: 12,
    padding: '13px 14px', fontSize: 15, color: '#0e1f13', outline: 'none',
    fontFamily: "'Barlow', sans-serif", width: '100%', boxSizing: 'border-box'
  },
  statHint: { fontSize: 10, color: '#6b7c6e', textAlign: 'center', marginTop: 4 },

  // ── Autocomplete dropdown ──────────────────────────────
  searchSpinner: {
    position: 'absolute', right: 14, top: '50%',
    transform: 'translateY(-50%)', fontSize: 14
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 12, marginTop: 4, zIndex: 100,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    overflow: 'hidden'
  },
  dropdownItem: {
    padding: '11px 14px', cursor: 'pointer', borderBottom: '1px solid #f4f7f5',
    transition: 'background 0.1s'
  },
  dropdownName: { fontWeight: 600, fontSize: 14, color: '#0e1f13' },
  dropdownClub: { fontSize: 11, color: '#1a7a3c', marginTop: 1, fontWeight: 600 },
  dropdownLocation: { fontSize: 11, color: '#6b7c6e', marginTop: 1 },
  dropdownEmpty: {
    padding: '14px', fontSize: 13, color: '#6b7c6e',
    textAlign: 'center', fontStyle: 'italic'
  },

  select: {
    background: 'white', border: '1.5px solid #e2ebe4', borderRadius: 12,
    padding: '13px 14px', fontSize: 15, color: '#0e1f13', outline: 'none',
    fontFamily: "'Barlow', sans-serif", width: '100%', boxSizing: 'border-box',
    appearance: 'none', cursor: 'pointer'
  },
  tourneyNote: {
    fontSize: 12, color: '#6b7c6e', fontStyle: 'italic', padding: '4px 2px'
  },
  tourneyConfirm: {
    fontSize: 12, color: '#1a7a3c', fontWeight: 600, padding: '4px 2px'
  },

  chip: {
    background: '#e8f5ed', color: '#1a7a3c', border: 'none', borderRadius: 20,
    padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif"
  },
  srcBtn: {
    flex: 1, background: 'white', border: '1.5px solid #e2ebe4', borderRadius: 10,
    padding: '10px 6px', fontSize: 11, fontWeight: 700, color: '#6b7c6e',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  srcBtnActive: { background: '#e8f5ed', borderColor: '#2aad56', color: '#1a7a3c' },

  // ── Photo ─────────────────────────────────────────────
  photoPickerBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    background: 'white', border: '1.5px dashed #c8d5ca', borderRadius: 12,
    padding: '20px', fontSize: 14, fontWeight: 600, color: '#6b7c6e',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  photoPreviewWrapper: { position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1.5px solid #e2ebe4' },
  photoPreview: { width: '100%', height: 200, objectFit: 'cover', display: 'block' },
  removePhotoBtn: {
    position: 'absolute', top: 10, right: 10,
    background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none',
    borderRadius: '50%', width: 28, height: 28, fontSize: 12, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },

  hcpBox: {
    background: '#e8f5ed', border: '1.5px solid #b3ddbf', borderRadius: 12,
    padding: '14px 16px', marginTop: 10
  },
  hcpTitle: { fontSize: 12, fontWeight: 700, color: '#1a7a3c', marginBottom: 10, letterSpacing: 0.5 },
  hcpGrid: {
    display: 'grid', gridTemplateColumns: '1fr auto',
    gap: '4px 16px', marginBottom: 10
  },
  hcpKey: { fontSize: 12, color: '#6b7c6e' },
  hcpVal: { fontSize: 12, fontWeight: 700, color: '#0e1f13', textAlign: 'right' },
  hcpResult: {
    borderTop: '1px solid #b3ddbf', paddingTop: 10,
    fontSize: 15, color: '#0e1f13', textAlign: 'center'
  },

  error: {
    background: 'rgba(232,64,64,0.1)', border: '1px solid rgba(232,64,64,0.3)',
    color: '#c0392b', borderRadius: 10, padding: '10px 14px', fontSize: 13
  },
  submitBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none', borderRadius: 14,
    padding: '18px', fontSize: 18, fontWeight: 900,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1,
    cursor: 'pointer', marginTop: 8
  }
}
